import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  Alert,
  FlatList,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Vehicle, Driver } from '../../types';
import { useVehicleStore } from '../../stores/vehicleStore';
import { useDriverStore } from '../../stores/driverStore';
import theme from '../../theme';

interface VehicleSwapSelectionScreenProps {
  navigation: any;
  route: {
    params?: {};
  };
}

type SelectionStep = 'current' | 'new' | 'confirm';

export const VehicleSwapSelectionScreen: React.FC<VehicleSwapSelectionScreenProps> = ({
  navigation,
}) => {
  const [currentStep, setCurrentStep] = useState<SelectionStep>('current');
  const [currentVehicle, setCurrentVehicle] = useState<Vehicle | null>(null);
  const [newVehicle, setNewVehicle] = useState<Vehicle | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const vehicles = useVehicleStore((state) => state.vehicles);
  const drivers = useDriverStore((state) => state.drivers);
  const loading = useVehicleStore((state) => state.loading);

  // Filter vehicles based on current step
  const getFilteredVehicles = () => {
    let vehiclesToShow: Vehicle[] = [];
    
    if (currentStep === 'current') {
      // Show only assigned vehicles
      vehiclesToShow = vehicles.filter(v => v.currentDriverId);
    } else if (currentStep === 'new') {
      // Show only available vehicles (not the current one)
      vehiclesToShow = vehicles.filter(v => 
        !v.currentDriverId && v.id !== currentVehicle?.id
      );
    }

    // Apply search filter
    return vehiclesToShow.filter(vehicle =>
      vehicle.licensePlate.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (vehicle.makeModel?.toLowerCase().includes(searchQuery.toLowerCase()) || false)
    );
  };

  const handleVehicleSelection = (vehicle: Vehicle) => {
    if (currentStep === 'current') {
      setCurrentVehicle(vehicle);
      setCurrentStep('new');
      setSearchQuery('');
    } else if (currentStep === 'new') {
      setNewVehicle(vehicle);
      setCurrentStep('confirm');
    }
  };

  const handleConfirmSwap = () => {
    if (!currentVehicle || !newVehicle) {
      Alert.alert('خطأ / Error', 'يجب اختيار المركبتين / Both vehicles must be selected');
      return;
    }

    // Navigate to the swap screen with selected vehicles
    navigation.navigate('VehicleSwap', {
      currentVehicleId: currentVehicle.id,
      newVehicleId: newVehicle.id,
    });
  };

  const handleBack = () => {
    if (currentStep === 'new') {
      setCurrentStep('current');
      setNewVehicle(null);
      setSearchQuery('');
    } else if (currentStep === 'confirm') {
      setCurrentStep('new');
    } else {
      navigation.goBack();
    }
  };

  const getAssignedDriver = (vehicleId: string): Driver | null => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (!vehicle || !vehicle.currentDriverId) return null;
    return drivers.find(d => d.id === vehicle.currentDriverId) || null;
  };

  const renderVehicleItem = ({ item }: { item: Vehicle }) => {
    const assignedDriver = getAssignedDriver(item.id);
    const isAssigned = !!item.currentDriverId;

    return (
      <TouchableOpacity
        style={styles.vehicleItem}
        onPress={() => handleVehicleSelection(item)}
      >
        <View style={styles.vehicleContent}>
          <View style={styles.vehicleHeader}>
            <Text style={styles.vehiclePlate}>{item.licensePlate}</Text>
            <View style={[
              styles.statusBadge,
              isAssigned ? styles.assignedBadge : styles.availableBadge
            ]}>
              <Text style={[
                styles.statusText,
                isAssigned ? styles.assignedText : styles.availableText
              ]}>
                {isAssigned ? 'مُعيَّنة / Assigned' : 'متاحة / Available'}
              </Text>
            </View>
          </View>
          
          <Text style={styles.vehicleModel}>{item.makeModel}</Text>
          <Text style={styles.vehicleYear}>{item.year}</Text>
          
          {assignedDriver && (
            <View style={styles.driverInfo}>
              <Ionicons name="person" size={16} color={theme.colors.textSecondary} />
              <Text style={styles.driverText}>
                {assignedDriver.name} - {assignedDriver.phone}
              </Text>
            </View>
          )}
        </View>
        
        <Ionicons name="chevron-forward" size={24} color={theme.colors.textSecondary} />
      </TouchableOpacity>
    );
  };

  const renderStepHeader = () => {
    let title = '';
    let subtitle = '';
    
    switch (currentStep) {
      case 'current':
        title = 'اختر المركبة الحالية / Select Current Vehicle';
        subtitle = 'اختر المركبة المُعيَّنة التي تريد إرجاعها / Select the assigned vehicle to return';
        break;
      case 'new':
        title = 'اختر المركبة الجديدة / Select New Vehicle';
        subtitle = 'اختر المركبة المتاحة للتعيين / Select the available vehicle to assign';
        break;
      case 'confirm':
        title = 'تأكيد التبديل / Confirm Swap';
        subtitle = 'تأكد من اختيار المركبات الصحيحة / Confirm the correct vehicles are selected';
        break;
    }

    return (
      <Card style={styles.stepHeader}>
        <Text style={styles.stepTitle}>{title}</Text>
        <Text style={styles.stepSubtitle}>{subtitle}</Text>
      </Card>
    );
  };

  const renderConfirmation = () => {
    const currentDriver = currentVehicle ? getAssignedDriver(currentVehicle.id) : null;

    return (
      <View style={styles.confirmationContainer}>
        <Card style={styles.confirmationCard}>
          <Text style={styles.confirmationTitle}>ملخص التبديل / Swap Summary</Text>
          
          <View style={styles.swapSummary}>
            {/* Current Vehicle */}
            <View style={styles.summarySection}>
              <Text style={styles.summaryLabel}>المركبة الحالية (سيتم إرجاعها) / Current Vehicle (To Return):</Text>
              <View style={styles.vehicleSummary}>
                <Text style={styles.summaryVehicle}>
                  {currentVehicle?.licensePlate} - {currentVehicle?.makeModel}
                </Text>
                {currentDriver && (
                  <Text style={styles.summaryDriver}>
                    السائق / Driver: {currentDriver.name}
                  </Text>
                )}
              </View>
            </View>

            <View style={styles.arrowContainer}>
              <Ionicons name="arrow-down" size={32} color={theme.colors.primary} />
            </View>

            {/* New Vehicle */}
            <View style={styles.summarySection}>
              <Text style={styles.summaryLabel}>المركبة الجديدة (سيتم تعيينها) / New Vehicle (To Assign):</Text>
              <View style={styles.vehicleSummary}>
                <Text style={styles.summaryVehicle}>
                  {newVehicle?.licensePlate} - {newVehicle?.makeModel}
                </Text>
                <Text style={styles.summaryDriver}>
                  السائق / Driver: {currentDriver?.name}
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.confirmationNote}>
            <Ionicons name="information-circle" size={20} color={theme.colors.warning} />
            <Text style={styles.noteText}>
              ستحتاج لتعبئة نموذجين: تسليم المركبة الحالية ثم استلام المركبة الجديدة
              {'\n'}
              You will need to fill two forms: delivery of current vehicle then receiving of new vehicle
            </Text>
          </View>

          <View style={styles.confirmationButtons}>
            <Button
              title="تأكيد البدء / Confirm & Start"
              onPress={handleConfirmSwap}
              variant="primary"
              style={styles.confirmButton}
            />
            <Button
              title="تعديل الاختيار / Change Selection"
              onPress={() => setCurrentStep('new')}
              variant="outline"
              style={styles.changeButton}
            />
          </View>
        </Card>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>جاري تحميل المركبات... / Loading vehicles...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleBack}>
          <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
        </TouchableOpacity>
        <View style={styles.headerContent}>
          <Text style={styles.headerTitle}>تبديل المركبة / Vehicle Swap</Text>
          <Text style={styles.headerSubtitle}>
            الخطوة {currentStep === 'current' ? '1' : currentStep === 'new' ? '2' : '3'} من 3
            {' / '}
            Step {currentStep === 'current' ? '1' : currentStep === 'new' ? '2' : '3'} of 3
          </Text>
        </View>
      </View>

      {renderStepHeader()}

      {currentStep === 'confirm' ? (
        renderConfirmation()
      ) : (
        <View style={styles.content}>
          {/* Search Bar */}
          <View style={styles.searchContainer}>
            <Ionicons name="search" size={20} color={theme.colors.textSecondary} />
            <TextInput
              style={styles.searchInput}
              placeholder="البحث برقم اللوحة أو الموديل / Search by plate or model"
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholderTextColor={theme.colors.textSecondary}
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')}>
                <Ionicons name="close" size={20} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            )}
          </View>

          {/* Vehicle List */}
          <FlatList
            data={getFilteredVehicles()}
            renderItem={renderVehicleItem}
            keyExtractor={(item) => item.id}
            style={styles.vehicleList}
            contentContainerStyle={styles.vehicleListContent}
            showsVerticalScrollIndicator={true}
            bounces={true}
            scrollEnabled={true}
            ListEmptyComponent={
              <View style={styles.emptyContainer}>
                <Ionicons name="car" size={64} color={theme.colors.textSecondary} />
                <Text style={styles.emptyText}>
                  {currentStep === 'current' 
                    ? 'لا توجد مركبات مُعيَّنة / No assigned vehicles'
                    : 'لا توجد مركبات متاحة / No available vehicles'
                  }
                </Text>
              </View>
            }
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
  },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 2,
  },
  stepHeader: {
    margin: 16,
    marginBottom: 8,
  },
  stepTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: 8,
  },
  stepSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  searchInput: {
    flex: 1,
    marginLeft: 8,
    fontSize: 16,
    color: theme.colors.textPrimary,
  },
  vehicleList: {
    flex: 1,
  },
  vehicleListContent: {
    flexGrow: 1,
    paddingBottom: 20,
  },
  vehicleItem: {
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  vehicleContent: {
    flex: 1,
  },
  vehicleHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  vehiclePlate: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  assignedBadge: {
    backgroundColor: theme.colors.success + '20',
  },
  availableBadge: {
    backgroundColor: theme.colors.warning + '20',
  },
  statusText: {
    fontSize: 12,
    fontWeight: '500',
  },
  assignedText: {
    color: theme.colors.success,
  },
  availableText: {
    color: theme.colors.warning,
  },
  vehicleModel: {
    fontSize: 16,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  vehicleYear: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginBottom: 8,
  },
  driverInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  driverText: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginLeft: 6,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 64,
  },
  emptyText: {
    fontSize: 16,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginTop: 16,
  },
  confirmationContainer: {
    flex: 1,
    paddingHorizontal: 16,
  },
  confirmationCard: {
    marginBottom: 16,
  },
  confirmationTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: 24,
  },
  swapSummary: {
    marginBottom: 24,
  },
  summarySection: {
    marginBottom: 16,
  },
  summaryLabel: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginBottom: 8,
  },
  vehicleSummary: {
    backgroundColor: theme.colors.background,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  summaryVehicle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  summaryDriver: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  arrowContainer: {
    alignItems: 'center',
    marginVertical: 8,
  },
  confirmationNote: {
    flexDirection: 'row',
    backgroundColor: theme.colors.warning + '10',
    padding: 12,
    borderRadius: 8,
    marginBottom: 24,
  },
  noteText: {
    flex: 1,
    fontSize: 14,
    color: theme.colors.textPrimary,
    lineHeight: 20,
    marginLeft: 8,
  },
  confirmationButtons: {
    gap: 12,
  },
  confirmButton: {
    marginBottom: 8,
  },
  changeButton: {
    marginBottom: 8,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: theme.colors.textSecondary,
  },
});